<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { getSubPrograms, getFilters } from './api';
import VFilters from './components/VFilters.vue';
import Results from './components/VResults.vue';
import type { SubPrograms, Filters } from './types/general';

const subPrograms = ref<SubPrograms>();
const filters = ref<Filters>();

const selectedFilters = ref<{
  location: string[];
  theme: string[];
  project_type: string[];
}>({
  location: [],
  theme: [],
  project_type: [],
});

const filtersRestored = ref(false);
const restoreSelectedFilters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const locationParam = urlParams.get('location');
  const themeParam = urlParams.get('theme');
  const projectTypeParam = urlParams.get('project_type');

  if (!urlParams.size) {
    filtersRestored.value = true;
    return;
  }

  if (locationParam) {
    selectedFilters.value.location = locationParam.split(',').map(String);
  }
  if (themeParam) {
    selectedFilters.value.theme = themeParam.split(',').map(String);
  }
  if (projectTypeParam) {
    selectedFilters.value.project_type = projectTypeParam.split(',').map(String);
  }

  filtersRestored.value = true;
};

onMounted(async () => {
  filters.value = await getFilters();
  subPrograms.value = await getSubPrograms();
  restoreSelectedFilters();
});

const updateUrl = () => {
  const { location, theme, project_type } = selectedFilters.value;
  const params = new URLSearchParams();

  if (location.length > 0) {
    params.set('location', location.join(','));
  }
  if (theme.length > 0) {
    params.set('theme', theme.join(','));
  }
  if (project_type.length > 0) {
    params.set('project_type', project_type.join(','));
  }

  const url = new URL(window.location.href);
  url.search = params.toString();

  window.history.replaceState({}, '', url.toString());
};

watch(
  selectedFilters,
  () => {
    updateUrl();
  },
  { deep: true }
);

const filteredResults = computed(() => {
  if (!subPrograms.value) return null;
  return subPrograms.value.items.filter((item) => {
    const location = selectedFilters.value.location;
    const theme = selectedFilters.value.theme;
    const project_type = selectedFilters.value.project_type;

    if (location.length === 0 && theme.length === 0 && project_type.length === 0) {
      return true;
    }

    if (location.length > 0 && !location.some((id) => item.countries.some((country) => country.id === id))) {
      return false;
    }

    if (theme.length > 0 && !theme.some((id) => item.theme.some((theme) => theme.id === id))) {
      return false;
    }

    if (project_type.length > 0 && !project_type.some((id) => item.project_type.some((type) => type.id === id))) {
      return false;
    }

    return true;
  });
});
</script>

<template>
  <VFilters
    v-if="filters && filtersRestored"
    :filters="filters"
    v-model="selectedFilters"
  ></VFilters>

  <Suspense>
    <template #default>
      <Results
        v-if="filteredResults"
        :results="filteredResults"
        :count="filteredResults.length"
      ></Results>
    </template>

    <template #fallback>
      <div class="loading">Loading...</div>
    </template>
  </Suspense>
</template>
